// Languages the marketing site is fully translated into, and which pages have a translated URL.
//
// English lives at the root (/pricing). Every other language lives under its own prefix
// (/hi/pricing, /ar/pricing, /es/pricing) so search engines can index each version. Only the
// pages listed in LOCALIZED_PATHS exist in other languages; everything else (blog, legal,
// comparison pages) is English-only and links to it from a translated page go to English.
//
// Adding a page here means every string on it must be translated in src/i18n/dict:
// the prerender (scripts/prerender.mjs) fails the build on any untranslated string.

import { FEATURE_PAGES } from '../data/featurePages';

export type SiteLocale = 'en' | 'hi' | 'ar' | 'es';

export const SITE_LOCALES: SiteLocale[] = ['en', 'hi', 'ar', 'es'];
export const PREFIXED_LOCALES: Exclude<SiteLocale, 'en'>[] = ['hi', 'ar', 'es'];

export const LOCALE_META: Record<SiteLocale, { name: string; nativeName: string; hrefLang: string; rtl: boolean; ogLocale: string }> = {
  en: { name: 'English', nativeName: 'English', hrefLang: 'en', rtl: false, ogLocale: 'en_US' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', hrefLang: 'hi', rtl: false, ogLocale: 'hi_IN' },
  ar: { name: 'Arabic', nativeName: 'العربية', hrefLang: 'ar', rtl: true, ogLocale: 'ar_AE' },
  es: { name: 'Spanish', nativeName: 'Español', hrefLang: 'es', rtl: false, ogLocale: 'es_ES' },
};

/** English paths that also exist in every other site locale. */
export const LOCALIZED_PATHS: string[] = [
  '/',
  '/pricing',
  '/gyms',
  '/for-coaches',
  '/client-app',
  '/ai-coach',
  '/features',
  ...FEATURE_PAGES.filter((f) => !f.englishOnly).map((f) => `/features/${f.slug}`),
];

const ORIGIN = 'https://trainzilla.app';

function normalize(path: string): string {
  const clean = path.split(/[?#]/)[0].replace(/\/+$/, '');
  return clean === '' ? '/' : clean;
}

export function isLocalizedPath(path: string): boolean {
  return LOCALIZED_PATHS.includes(normalize(path));
}

/** Split "/hi/pricing" into { locale: 'hi', path: '/pricing' }. */
export function splitLocale(pathname: string): { locale: SiteLocale; path: string } {
  const m = pathname.match(/^\/(hi|ar|es)(?=\/|$)(.*)$/);
  if (!m) return { locale: 'en', path: normalize(pathname) };
  return { locale: m[1] as SiteLocale, path: normalize(m[2] || '/') };
}

/** The URL of `path` in `locale`, or the English URL when that page isn't translated. */
export function localePath(path: string, locale: SiteLocale): string {
  const [base, suffix = ''] = path.split(/(?=[?#])/);
  const p = normalize(base);
  if (locale === 'en' || !isLocalizedPath(p)) return p + suffix;
  return (p === '/' ? `/${locale}` : `/${locale}${p}`) + suffix;
}

/** hreflang cluster for a localized page (absolute URLs, x-default = English). */
export function localeAlternates(path: string): { hrefLang: string; href: string }[] {
  const p = normalize(path);
  const abs = (loc: SiteLocale) => ORIGIN + (localePath(p, loc) === '/' ? '/' : localePath(p, loc));
  return [
    ...SITE_LOCALES.map((loc) => ({ hrefLang: LOCALE_META[loc].hrefLang, href: abs(loc) })),
    { hrefLang: 'x-default', href: abs('en') },
  ];
}

export function absoluteLocaleUrl(path: string, locale: SiteLocale): string {
  const p = localePath(path, locale);
  return ORIGIN + (p === '/' ? '/' : p);
}
